import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { createMqStyles } from '@fhs/ui';
import { IDonationAtCheckoutWithContentProps } from '@fhs-legacy/frontend/src/pages/cart/donation-at-checkout/types';
import { useMenuOptionsContext } from '@fhs-legacy/frontend/src/state/menu-options';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import useDonation from '@fhs-legacy/frontend/src/state/order/hooks/use-donation';
import { useUIContext } from '@fhs-legacy/frontend/src/state/ui';
import { DONATION_BUTTON_GROUP } from '@fhs-legacy/frontend/src/utils/test-ids';
import { ToggleButtonGroup } from '@fhs-legacy/universal-components';
import { IItem } from '@rbi-ctg/menu';

interface IDonationButtonsProps {
  donationItems: IItem[];
  setIsBadContinueAttempt: React.Dispatch<React.SetStateAction<boolean>>;
}

const ZERO_AMOUNT_DONATION = '0';

export default function DonationButtons({
  donationItems,
  setIsBadContinueAttempt,
}: IDonationButtonsProps) {
  const mqStyles = useMqStyles();
  const { pricingFunction } = useMenuOptionsContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();

  const { setIsDonationZeroAmountSelected, selectedDonationId, setSelectedDonationId } =
    useOrderContext();

  const { handleAddDonationCartEntry, handleRemoveDonationCartEntry, selectedDonationCartEntry } =
    useDonation({
      donationItems,
    });

  const allDonationOptions: IDonationAtCheckoutWithContentProps['donationOptions'] = [
    {
      label: 'None',
      value: ZERO_AMOUNT_DONATION,
    },
    ...(donationItems.map(item => ({
      label: formatCurrencyForLocale(pricingFunction(item)),
      value: item._id,
      isDisabled: false,
    })) as IDonationAtCheckoutWithContentProps['donationOptions']),
  ];

  const handleDonationToggleChange = useCallback(
    (newSelectedDonationId?: string) => {
      // any selection made, remove bad continue attempt alert
      setIsBadContinueAttempt(false);

      // user removed donation, remove previous donation cart entry
      if (ZERO_AMOUNT_DONATION === newSelectedDonationId) {
        setSelectedDonationId(ZERO_AMOUNT_DONATION);

        if (selectedDonationCartEntry) {
          handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry.cartId });
        }
        setIsDonationZeroAmountSelected(true);
        return;
      }

      const optionItem = donationItems.find(item => item?._id === newSelectedDonationId);
      if (!optionItem) {
        setSelectedDonationId('');
        return;
      }
      setSelectedDonationId(optionItem?._id);
      setIsDonationZeroAmountSelected(false);
      handleAddDonationCartEntry(optionItem);
    },
    [
      donationItems,
      handleAddDonationCartEntry,
      handleRemoveDonationCartEntry,
      selectedDonationCartEntry,
      setIsDonationZeroAmountSelected,
      setIsBadContinueAttempt,
      setSelectedDonationId,
    ]
  );

  return (
    <ToggleButtonGroup
      _toggleButton={{
        minWidth: 50,
        maxWidth: 213,
        borderRadius: 100,
      }}
      options={allDonationOptions}
      onChangeValue={handleDonationToggleChange}
      value={selectedDonationId}
      accessibilityHint={formatMessage({ id: 'selectDonationAmount' })}
      accessibilityLabel={formatMessage({ id: 'selectDonationAmount' })}
      style={mqStyles.toggleButtonGroup}
      boldButtonText
      testID={DONATION_BUTTON_GROUP}
    />
  );
}

const useMqStyles = createMqStyles({
  toggleButtonGroup: {
    $base: {
      paddingVertical: 10,
      gap: 8,
      justifyContent: 'center',
      marginTop: 8,
    },
  },
});
